import {
  GavelOutlined,
  HelpOutline,
  InfoOutlined,
  KeyboardDoubleArrowRight,
  OpenInNew,
  PrivacyTipOutlined,
  ShieldOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Switch,
  Theme,
  Toolbar,
  Typography,
  useColorScheme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import LanguageDetector from 'i18next-browser-languagedetector';
import { useEffect, useState, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { BPS_PRIVACY_POLICY_PATH } from '../../../Pages/BpsPrivacyPolicyPage';
import { useAppDispatch, useAppSelector } from '../../../redux/ReduxHooks';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { projectSlice } from '../../../redux/ProjectSlice';
import { ApplicationActions } from '../../../utils/constants';
import { LanguagePreference, readLanguagePreference } from '../../../utils/language-config';
import i18n from '../../../i18n';
import LanguageSelector from './LanguageSelector';
import { settingsSectionBodyInsetSx } from './settingsSectionLayout';
import ThemeButton from './ThemeButton';

const LANGUAGE_PREFERENCE_KEY = 'i18nextLngPreference';

const SettingsDrawer = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const { mode, setMode } = useColorScheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language || 'en');
  const [languagePreference, setLanguagePreference] = useState<LanguagePreference | undefined>(() => {
    return readLanguagePreference(localStorage, LANGUAGE_PREFERENCE_KEY);
  });

  const openDrawer = useAppSelector((state) => state.project.openDrawer);
  const { autoSwapToDashboard } = useAppSelector((state) => state.application);

  // Store the original browser language for comparison
  const [originalBrowserLanguage] = useState(() => {
    const languageDetector = new LanguageDetector(null, {
      order: ['localStorage', 'querystring', 'cookie', 'navigator', 'sessionStorage', 'htmlTag', 'path', 'subdomain'],
    });
    return (languageDetector.detect() as string) || 'en';
  });

  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(openDrawer === ApplicationActions.Drawer.SETTINGS_DRAWER);
  }, [openDrawer]);

  useEffect(() => {
    let initialLanguage;
    if (languagePreference === 'browser') {
      // User wants browser language
      initialLanguage = originalBrowserLanguage;
    } else if (languagePreference) {
      // User has a specific language preference
      initialLanguage = languagePreference;
    } else {
      // No preference stored, use i18n's current language (which uses browser detection)
      initialLanguage = i18n.language;
    }

    setCurrentLanguage(initialLanguage);

    // If we have a stored preference and i18n is initialized, make sure it matches
    if (i18n.isInitialized && languagePreference && languagePreference !== 'browser' && i18n.language !== initialLanguage) {
      i18n.changeLanguage(initialLanguage);
    }

    const handleLanguageChange = (lng: string) => {
      setCurrentLanguage(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [languagePreference, originalBrowserLanguage]);

  // Focus the close button when drawer opens
  useEffect(() => {
    if (open && closeButtonRef.current) {
      setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const handleClose = () => {
    dispatch(projectSlice.actions.onOpenDrawer(null));
  };

  // Handle keydown events to trap focus within the drawer
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!open) return;

    // Prevent tab from moving focus outside the drawer
    if (event.key === 'Tab') {
      const focusableElements = drawerRef.current?.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');

      if (!focusableElements?.length) return;

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      // If shift+tab on first element, move to last element
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
      // If tab on last element, move to first element
      else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    // Prevent Enter key from closing the drawer unless a button is specifically clicked
    if (event.key === 'Enter' && !(event.target as HTMLElement).closest('button')) {
      event.preventDefault();
    }
  };

  const handleOpenAcknowledgmentsDialog = () => {
    dispatch(applicationSlice.actions.onOpenDialog(ApplicationActions.Dialogs.ACKNOWLEDGMENTS_DIALOG));
    handleClose();
  };

  const handleOpenBpsPrivacyPolicy = () => {
    const url = new URL(BPS_PRIVACY_POLICY_PATH, window.location.origin).href;
    window.open(url, '_blank', 'noopener,noreferrer');
    handleClose();
  };

  const openExternalUrl = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleThemeChange = (newMode: 'light' | 'dark' | 'system') => {
    setMode(newMode);
  };

  const handleLanguageChange = (newLanguage: LanguagePreference) => {
    // If "browser" is selected, use the original browser language for i18n
    // but store "browser" as the preference to indicate user wants browser language
    const actualLanguage = newLanguage === 'browser' ? originalBrowserLanguage : newLanguage;

    i18n.changeLanguage(actualLanguage);
    setCurrentLanguage(actualLanguage);

    // Save the user's preference (either "browser" or specific language)
    setLanguagePreference(newLanguage);
    localStorage.setItem(LANGUAGE_PREFERENCE_KEY, newLanguage);

    handleClose();
  };

  const handleToggleAutoSwapToDashboard = () => {
    dispatch(applicationSlice.actions.onSetAutoSwapToDashboard(!autoSwapToDashboard));
  };

  const drawerSx = {
    flexShrink: 0,
    zIndex: (theme: Theme) => theme.zIndex.drawer + 2,
    '& .MuiDrawer-paper': {
      width: isSmallScreen ? '100%' : isMediumScreen ? '300px' : '450px',
      boxSizing: 'border-box',
      bottom: 0,
      top: 'unset',
      bgcolor: 'background.paper',
      overflow: 'hidden',
    },
  };

  const dividerSx = {
    borderColor: 'divider',
    opacity: 0.8,
    margin: '8px 0',
  };

  const sectionTitleSx = {
    fontSize: isSmallScreen ? '16px' : isMediumScreen ? '18px' : '20px',
    paddingLeft: '8px',
    paddingRight: '8px',
    paddingTop: 0,
  };

  const linkButtonSx = {
    justifyContent: 'space-between',
    borderRadius: 2,
    paddingX: 2,
    paddingY: 1.25,
    textTransform: 'none',
    fontWeight: 600,
  };

  if (!mode) {
    return null;
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        sx={drawerSx}
        role="dialog"
        aria-modal="true"
        aria-label={t('aria.settings_drawer', 'Settings drawer')}
        onKeyDown={handleKeyDown}
        ref={drawerRef}
        disableAutoFocus={false}
        disableEnforceFocus={false}
        disableRestoreFocus={false}
      >
        <Toolbar />
        <Box
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          {/* Drawer Title Section */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            padding={1.75}
            position="relative"
            sx={{
              width: '100%',
              flexShrink: 0,
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '95%',
                height: '2px',
                backgroundColor: 'accent.main',
              },
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="sectionTitle">
                <Trans i18nKey="button.settings">Settings</Trans>
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              aria-label={t('aria-label.close', 'close')}
              sx={{ position: 'absolute', right: 8 }}
              ref={closeButtonRef}
            >
              <KeyboardDoubleArrowRight />
            </IconButton>
          </Box>

          {/* Scrollable Content Area */}
          <Box
            sx={{
              flexGrow: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              bgcolor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              px: 2,
            }}
          >
            <Box width="100%" maxWidth="92%" marginTop={isSmallScreen ? '8px' : '17px'}>
              <Paper elevation={2}>
                <Box padding={isSmallScreen ? '16px' : '24px'}>
                  <Stack spacing={3}>
                    {/* Theme Section */}
                    <Box>
                      <Typography variant="subHeaderTitle" gutterBottom sx={sectionTitleSx}>
                        <Trans i18nKey="theme.title">Theme</Trans>
                      </Typography>
                      <ThemeButton onThemeChange={handleThemeChange} mode={mode || 'system'} />
                    </Box>

                    <Divider sx={dividerSx} />

                    {/* Auto Swap to Dashboard Section */}
                    <Box>
                      <Typography variant="subHeaderTitle" gutterBottom sx={sectionTitleSx}>
                        <Trans i18nKey="settings.dashboard">Dashboard</Trans>
                      </Typography>
                      <Box sx={settingsSectionBodyInsetSx}>
                        <Box
                          sx={{
                            border: 1,
                            borderColor: autoSwapToDashboard ? 'accent.main' : 'divider',
                            borderRadius: 2,
                            bgcolor: 'action.hover',
                            px: 2,
                            py: 1.5,
                          }}
                        >
                          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'primary.main',
                                }}
                              >
                                <Trans i18nKey="settings.showDashboardOnRun.description">
                                  Automatically switch to the dashboard when your program starts running.
                                </Trans>
                              </Typography>
                            </Box>
                            <Switch checked={autoSwapToDashboard} onChange={handleToggleAutoSwapToDashboard} />
                          </Stack>
                        </Box>
                      </Box>
                    </Box>

                    <Divider sx={dividerSx} />

                    {/* Support And Legal Section */}
                    <Box>
                      <Typography variant="subHeaderTitle" gutterBottom sx={sectionTitleSx}>
                        <Trans i18nKey="settings.resources">Resources</Trans>
                      </Typography>
                      <Stack spacing={1.25} sx={settingsSectionBodyInsetSx}>
                        <Button
                          size="large"
                          variant="outlined"
                          fullWidth
                          startIcon={<HelpOutline />}
                          endIcon={<OpenInNew />}
                          sx={linkButtonSx}
                          onClick={() => openExternalUrl('https://help.sphero.com/sphero-support/blueprint-products')}
                        >
                          <Trans i18nKey="button.support">Support</Trans>
                        </Button>
                        <Button
                          size="large"
                          variant="outlined"
                          fullWidth
                          startIcon={<ShieldOutlined />}
                          endIcon={<OpenInNew />}
                          sx={linkButtonSx}
                          onClick={handleOpenBpsPrivacyPolicy}
                        >
                          <Trans i18nKey="menu.bps-privacy-policy">Blueprint Studio Privacy Policy</Trans>
                        </Button>
                        <Button
                          size="large"
                          variant="outlined"
                          fullWidth
                          startIcon={<PrivacyTipOutlined />}
                          endIcon={<OpenInNew />}
                          sx={linkButtonSx}
                          onClick={() => openExternalUrl('https://sphero.com/pages/privacy')}
                        >
                          <Trans i18nKey="menu.general-privacy-policy">General Privacy Policy</Trans>
                        </Button>
                        <Button
                          size="large"
                          variant="outlined"
                          fullWidth
                          startIcon={<GavelOutlined />}
                          endIcon={<OpenInNew />}
                          sx={linkButtonSx}
                          onClick={() => openExternalUrl('https://sphero.com/pages/terms-of-use')}
                        >
                          <Trans i18nKey="menu.terms-of-use">Terms of Use</Trans>
                        </Button>
                        <Button
                          size="large"
                          variant="outlined"
                          fullWidth
                          startIcon={<InfoOutlined />}
                          sx={{ ...linkButtonSx, justifyContent: 'center' }}
                          onClick={handleOpenAcknowledgmentsDialog}
                        >
                          <Trans i18nKey="aboutDialog.menu.acknowledgments">Acknowledgments</Trans>
                        </Button>
                      </Stack>
                    </Box>

                    <Divider sx={dividerSx} />

                    {/* Language Section */}
                    <Box>
                      <Typography variant="subHeaderTitle" gutterBottom sx={sectionTitleSx}>
                        <Trans i18nKey="settings.languageSelector">Language</Trans>
                      </Typography>
                      <LanguageSelector
                        currentLanguage={currentLanguage}
                        onLanguageChange={handleLanguageChange}
                        languagePreference={languagePreference}
                      />
                    </Box>
                  </Stack>
                </Box>
              </Paper>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default SettingsDrawer;
